import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Unified FAQ Schema - Consolidates all FAQs into single FAQPage
 * Prevents "Duplicate field FAQPage" error in Google Search Console
 * 
 * Combines:
 * - Dynamic FAQs from API
 * - Rodent-specific voice search FAQs (static)
 * - Homepage FAQs (static fallback)
 */
const UnifiedFAQSEO = ({ dynamicFaqs = [], includeRodentFAQs = true }) => {
  const businessName = 'NY Pest & Exterminating';
  const phone = '(646) 209-9955';

  // Rodent-specific FAQs for voice search
  const rodentFAQs = includeRodentFAQs ? [
    {
      question: "Who is the best rodent exterminator in NYC?",
      answer: `${businessName} is NYC's top-rated rodent exterminator. We offer same-day rat and mice removal across all 5 boroughs - Manhattan, Brooklyn, Queens, Bronx, and Staten Island. Licensed, insured, and eco-friendly. Call ${phone} for emergency rodent control.`
    },
    {
      question: "How much does rat removal cost in New York City?",
      answer: `Rat removal costs in NYC typically range from $200-$500 for initial treatment, depending on infestation severity. NY Pest & Exterminating offers free inspections and competitive pricing. Call ${phone} for a free quote on rodent extermination services.`
    },
    {
      question: "Is there emergency rat control available in NYC?",
      answer: `Yes! ${businessName} provides 24/7 emergency rat control throughout New York City. We offer same-day service in Manhattan, Brooklyn, Queens, Bronx, and Staten Island. Call ${phone} anytime for immediate rodent removal.`
    },
    {
      question: "How do I find a mice exterminator near me?",
      answer: `For a licensed mice exterminator near you in NYC, call ${businessName} at ${phone}. We serve all neighborhoods in Manhattan, Brooklyn, Queens, Bronx, and Staten Island with same-day availability. Eco-friendly and pet-safe mouse removal.`
    },
    {
      question: "What is the best way to get rid of rats in an apartment in Manhattan?",
      answer: `Professional rat control is the most effective solution for Manhattan apartments. ${businessName} uses eco-friendly methods safe for apartment buildings: sealing entry points, baiting, trapping, and exclusion. We serve all Manhattan neighborhoods. Call ${phone}.`
    }
  ] : [];

  // Combine all FAQs - API FAQs take priority, then rodent FAQs
  const allFAQs = [...dynamicFaqs, ...rodentFAQs];

  // Sort dynamic FAQs by order if available
  const sortedFaqs = allFAQs.sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    return 0;
  });

  if (sortedFaqs.length === 0) return null;

  // Single unified FAQPage schema
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": sortedFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Voice search keywords
  const voiceSearchKeywords = sortedFaqs
    .map(faq => faq.question.toLowerCase())
    .join(', ');

  // Extract categories
  const categories = [...new Set(sortedFaqs
    .filter(faq => faq.category)
    .map(faq => faq.category)
  )].join(', ');

  return (
    <Helmet>
      {/* Voice Search Meta Tags */}
      <meta name="faq-questions" content={voiceSearchKeywords} />
      {categories && <meta name="faq-categories" content={categories} />}
      <meta name="voice-search-optimized" content="true" />
      <meta name="speakable" content="true" />
      <meta name="faq-count" content={sortedFaqs.length} />
      
      {/* AI Search Optimization */}
      <meta name="ai-search-topics" content={`pest control FAQ, ${categories}, NYC exterminator questions, rodent control, emergency services`} />
      
      {/* Single Unified FAQ Schema - Prevents Duplicate FAQPage Error */}
      <script type="application/ld+json">
        {JSON.stringify(faqPageSchema)}
      </script>
    </Helmet>
  );
};

export default UnifiedFAQSEO;
